import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutGrid, Users, Package, Inbox, History, Bell, ListChecks,
  Building2, Boxes, LogOut, Menu, X, Store as StoreIcon, Upload, GitBranch,
  FileSpreadsheet, ShieldAlert, ScanLine, Trash2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { storeAbbr } from "../lib/labels";
import {
  TENANT_CATALOG_ONBOARDING_ENABLED,
  isTenantHqNavItemVisible,
} from "../lib/tenantCatalogAvailability";

// Each item carries a `roles` list. The sidebar filters by the current
// user's role so managers see ONLY the store-level surface and admins
// see everything. Backend gates are still authoritative — this is UX.
export const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutGrid, end: true, testid: "sidebar-dashboard", roles: ["admin", "manager"] },
  { to: "/admin/scan", label: "Scan", icon: ScanLine, testid: "sidebar-scan", roles: ["admin", "manager"] },
  { to: "/admin/inventory", label: "Inventory", icon: Boxes, testid: "sidebar-inventory", roles: ["admin", "manager"] },
  { to: "/admin/removals", label: "Removal Log", icon: Trash2, testid: "sidebar-removals", roles: ["admin", "manager"] },
  { to: "/admin/users", label: "Employees", icon: Users, testid: "sidebar-users", roles: ["admin", "manager"] },
  { to: "/admin/stores", label: "Stores", icon: StoreIcon, testid: "sidebar-stores", roles: ["admin"] },
  { to: "/admin/products", label: "Products", icon: Package, testid: "sidebar-products", roles: ["admin"] },
  { to: "/admin/catalog-import", label: "Catalog Import", icon: Upload, testid: "sidebar-catalog-import", roles: ["admin"], available: TENANT_CATALOG_ONBOARDING_ENABLED },
  { to: "/admin/catalog-import-csv", label: "CSV Import", icon: FileSpreadsheet, testid: "sidebar-catalog-import-csv", roles: ["admin"], available: TENANT_CATALOG_ONBOARDING_ENABLED },
  { to: "/admin/department-mapping", label: "Department Mapping", icon: GitBranch, testid: "sidebar-department-mapping", roles: ["admin"], available: TENANT_CATALOG_ONBOARDING_ENABLED },
  { to: "/admin/requests", label: "Product Requests", icon: Inbox, testid: "sidebar-requests", roles: ["admin", "manager"] },
  { to: "/admin/review-queue", label: "Review Queue", icon: ListChecks, testid: "sidebar-review", roles: ["admin"] },
  { to: "/admin/departments", label: "Departments", icon: Building2, testid: "sidebar-departments", roles: ["admin"] },
  { to: "/admin/history", label: "Action History", icon: History, testid: "sidebar-history", roles: ["admin", "manager"] },
  { to: "/admin/notifications", label: "Notifications", icon: Bell, testid: "sidebar-notifications", roles: ["admin", "manager"] },
  { to: "/admin/notification-policy", label: "Notification Policy", icon: ShieldAlert, testid: "sidebar-notification-policy", roles: ["admin"] },
];

export function getAdminNavForRole(role) {
  return ADMIN_NAV.filter((item) => isTenantHqNavItemVisible(item, role));
}

function NavItems({ onNavigate, variant, role }) {
  const items = getAdminNavForRole(role);
  return (
    <ul className="flex flex-col">
      {items.map(({ to, label, icon: Icon, end, testid }) => (
        <li key={to}>
          <NavLink
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={variant === "mobile" ? `${testid}-mobile` : testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-6 py-3.5 font-display uppercase text-sm tracking-wide border-l-4 transition-colors ${
                isActive
                  ? "bg-ink text-white border-brand"
                  : "text-ink/70 border-transparent hover:bg-secondary"
              }`
            }
          >
            <Icon size={18} strokeWidth={2.25} />
            {label}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function ConsoleHeader({ user }) {
  const isManager = user?.role === "manager";
  const abbr = storeAbbr(user);
  const abbrIsUnknown = abbr === "STORE UNKNOWN";
  return (
    <div className="px-6 py-6 border-b border-border" data-testid="console-header">
      <div className="font-display font-black text-2xl tracking-tight">
        Shelf<span className="text-brand">Life</span>
      </div>
      <div className="mt-1 data-sku flex items-center gap-2 flex-wrap" data-testid="console-header-subtitle">
        <span>{isManager ? "Store Manager" : "Admin Console"}</span>
        <span aria-hidden="true">·</span>
        <span
          className={`font-mono text-[11px] font-bold tracking-wider ${abbrIsUnknown ? "text-shelf-expired" : "text-brand"}`}
          data-testid="console-header-store-abbr"
          title={user?.store_name || (user?.role === "admin" ? "Headquarters" : "")}
        >
          {abbr}
        </span>
      </div>
      {isManager && user?.store_name && (
        <div
          className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 bg-brand/10 border border-brand/30 rounded-sm"
          data-testid="manager-store-badge"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand" aria-hidden="true"></span>
          <span className="font-mono text-[11px] uppercase tracking-wider text-ink truncate max-w-[160px]">
            {user.store_name}
          </span>
        </div>
      )}
    </div>
  );
}

export function AdminSidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const onLogout = async () => {
    const res = await logout();
    if (!res.ok && res.pending) {
      const ok = window.confirm(
        `You have ${res.pending} unsynced action${res.pending === 1 ? "" : "s"}. Continue?`,
      );
      if (!ok) return;
      await logout({ force: true });
    }
    nav("/login", { replace: true });
  };

  return (
    <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 bg-white border-r border-border flex-col z-30" data-testid="admin-sidebar">
      <ConsoleHeader user={user} />
      <nav className="flex-1 py-2 overflow-y-auto">
        <NavItems variant="desktop" role={user?.role} />
      </nav>
      <div className="border-t border-border p-4">
        <div className="px-2 pb-3">
          <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Signed in as</div>
          <div className="font-display font-bold text-sm truncate">{user?.email}</div>
        </div>
        <button onClick={onLogout} className="btn-secondary h-11 w-full" data-testid="sidebar-logout">
          <LogOut size={16} /> Sign out
        </button>
      </div>
    </aside>
  );
}

export function AdminTopBar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const abbr = storeAbbr(user);
  const abbrIsUnknown = abbr === "STORE UNKNOWN";

  // close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const onLogout = async () => {
    setOpen(false);
    const res = await logout();
    if (!res.ok && res.pending) {
      const ok = window.confirm(
        `You have ${res.pending} unsynced action${res.pending === 1 ? "" : "s"}. Continue?`,
      );
      if (!ok) return;
      await logout({ force: true });
    }
    nav("/login", { replace: true });
  };

  return (
    <>
      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-border px-4 h-14 flex items-center justify-between" data-testid="admin-topbar">
        <button
          onClick={() => setOpen(true)}
          className="h-10 w-10 -ml-2 flex items-center justify-center"
          aria-label="Open menu"
          data-testid="admin-menu-button"
        >
          <Menu size={22} strokeWidth={2.5} />
        </button>
        <div className="font-display font-black text-lg flex items-baseline gap-2">
          <span>Shelf<span className="text-brand">Life</span></span>
          <span
            className={`font-mono text-[11px] font-bold tracking-wider ${abbrIsUnknown ? "text-shelf-expired" : "text-brand"}`}
            data-testid="admin-topbar-store-abbr"
            title={user?.store_name || (user?.role === "admin" ? "Headquarters" : "")}
          >
            · {abbr}
          </span>
        </div>
        <div className="w-10 flex justify-end">
          <span className="data-sku hidden xs:inline">{user?.email}</span>
        </div>
      </div>

      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex" data-testid="admin-drawer">
          <div className="absolute inset-0 bg-ink/60" onClick={() => setOpen(false)} />
          <aside className="relative bg-white w-80 max-w-[85%] h-full flex flex-col animate-slide-up shadow-xl">
            <div className="px-6 py-5 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-display font-black text-xl tracking-tight">
                  Shelf<span className="text-brand">Life</span>
                </div>
                <div className="data-sku flex items-center gap-2 flex-wrap" data-testid="mobile-console-subtitle">
                  <span>{user?.role === "manager" ? "Store Manager" : "Admin Console"}</span>
                  <span aria-hidden="true">·</span>
                  <span
                    className={`font-mono text-[11px] font-bold tracking-wider ${abbrIsUnknown ? "text-shelf-expired" : "text-brand"}`}
                    data-testid="mobile-console-store-abbr"
                    title={user?.store_name || (user?.role === "admin" ? "Headquarters" : "")}
                  >
                    {abbr}
                  </span>
                </div>
                {user?.role === "manager" && user?.store_name && (
                  <div
                    className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 bg-brand/10 border border-brand/30 rounded-sm"
                    data-testid="manager-store-badge-mobile"
                  >
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand" aria-hidden="true"></span>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink truncate max-w-[140px]">
                      {user.store_name}
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setOpen(false)}
                className="h-10 w-10 flex items-center justify-center"
                aria-label="Close menu"
                data-testid="admin-menu-close"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 py-2 overflow-y-auto">
              <NavItems onNavigate={() => setOpen(false)} variant="mobile" role={user?.role} />
            </nav>
            <div className="border-t border-border p-4">
              <div className="px-2 pb-3">
                <div className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">Signed in as</div>
                <div className="font-display font-bold text-sm truncate" data-testid="admin-drawer-email">{user?.email}</div>
              </div>
              <button onClick={onLogout} className="btn-secondary h-11 w-full" data-testid="admin-drawer-logout">
                <LogOut size={16} /> Sign out
              </button>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
